import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { leagueCustomerMembershipsRoute } from "network/api-routes";
import { playerDisplayName } from "../financial/finance-descriptors";
import { membershipRequestPayload, replacementTarget } from "./create-membership";

const currentMonth = () => dayjs().format("YYYY-MM");

const firstError = (data) => {
  const errors = data?.errors || {};
  const key = Object.keys(errors)[0];
  return key ? errors[key]?.[0] : data?.message;
};

const CreateMembershipDialog = ({ customerId, player, open, onClose, onCreated }) => {
  const intl = useIntl();
  const { user } = useContext(UserContext);
  const [memberNumber, setMemberNumber] = useState("");
  const [startMonth, setStartMonth] = useState(currentMonth());
  const [conflict, setConflict] = useState(null);
  const [keeperId, setKeeperId] = useState(null);
  const [replacement, setReplacement] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMemberNumber(player.member_number || "");
    setStartMonth(currentMonth());
    setConflict(null);
    setKeeperId(null);
    setReplacement("");
    setError(null);
  }, [open, player.member_number]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await performRequest(
        "POST",
        leagueCustomerMembershipsRoute(customerId),
        membershipRequestPayload({ player, memberNumber, startMonth, conflict, keeperId, replacement }),
        true,
        user,
      );
      onCreated();
    } catch (requestError) {
      const data = requestError?.response?.data;
      if (Array.isArray(data?.member_number_conflicts) && data.member_number_conflicts.length > 0) {
        setConflict({ number: data.member_number, holders: data.member_number_conflicts });
        setKeeperId(player.id);
      } else {
        setError(firstError(data) || intl.formatMessage({ id: "gestion.error.generic" }));
      }
    } finally {
      setSaving(false);
    }
  };

  const target = conflict ? replacementTarget({ player, conflict, keeperId }) : null;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{intl.formatMessage({ id: "gestion.accountFinance.createMembership" })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {!conflict && (
            <>
              <TextField
                label={intl.formatMessage({ id: "gestion.players.memberNumber" })}
                value={memberNumber}
                onChange={(event) => setMemberNumber(event.target.value)}
                placeholder={intl.formatMessage({ id: "gestion.accountFinance.memberNumberAuto" })}
                helperText={intl.formatMessage({ id: "gestion.accountFinance.memberNumberAutoHelp" })}
                fullWidth
              />
              <TextField
                type="month"
                label={intl.formatMessage({ id: "gestion.accountFinance.firstBilledMonth" })}
                value={startMonth}
                onChange={(event) => setStartMonth(event.target.value)}
                helperText={intl.formatMessage({ id: "gestion.accountFinance.firstBilledMonthHelp" })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </>
          )}
          {conflict && (
            <>
              <Alert severity="warning">
                {intl.formatMessage(
                  { id: "gestion.accountFinance.memberNumberConflict" },
                  { number: conflict.number },
                )}
              </Alert>
              <RadioGroup value={String(keeperId)} onChange={(event) => setKeeperId(Number(event.target.value))}>
                <FormControlLabel
                  value={String(player.id)}
                  control={<Radio />}
                  label={intl.formatMessage(
                    { id: "gestion.accountFinance.memberNumberKeeperThis" },
                    { name: playerDisplayName(player) },
                  )}
                />
                {conflict.holders.map((holder) => (
                  <FormControlLabel
                    key={holder.id}
                    value={String(holder.id)}
                    control={<Radio />}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{`${playerDisplayName(holder)} · ${holder.member_number}`}</span>
                        {holder.has_membership && (
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={intl.formatMessage({ id: "gestion.accountFinance.hasMembership" })}
                          />
                        )}
                        {holder.archived && (
                          <Chip size="small" label={intl.formatMessage({ id: "gestion.accountFinance.archived" })} />
                        )}
                      </Stack>
                    }
                  />
                ))}
              </RadioGroup>
              {target && (
                <TextField
                  label={intl.formatMessage(
                    { id: "gestion.accountFinance.replacementMemberNumber" },
                    { name: playerDisplayName(target.loser) },
                  )}
                  value={replacement}
                  onChange={(event) => setReplacement(event.target.value)}
                  helperText={intl.formatMessage({ id: target.helpKey }, { number: player.member_number })}
                  fullWidth
                />
              )}
              {!target && (
                <RevaText variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: "gestion.accountFinance.replacementAutoMany" })}
                </RevaText>
              )}
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {intl.formatMessage({ id: "gestion.accountFinance.cancel" })}
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {intl.formatMessage({ id: "gestion.accountFinance.createMembership" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMembershipDialog;
